import React, { useEffect, useMemo, useState } from 'react';
import { clearEntryUpdateWaitRequest, confirmEntryUpdateWait } from '../utils/api';
import './ConversationIngestModal.css';

function formatFieldValue(value) {
  if (value == null || String(value).trim() === '') return '(empty)';
  if (typeof value === 'object') {
    try { return JSON.stringify(value); } catch (_) { return String(value); }
  }
  return String(value);
}

/**
 * Confirm UI when an entry_update tool proposes field writes.
 */
export default function EntryUpdateWaitModal({
  open,
  onClose,
  sessionId,
  waitRequest = null,
  onConfirmed = null,
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [fieldChanges, setFieldChanges] = useState([]);

  const title = useMemo(() => {
    const slug = waitRequest?.tool_slug || 'entry update';
    const isCreate = waitRequest?.create === true || waitRequest?.op === 'create';
    const label = waitRequest?.label || waitRequest?.entry_id || (isCreate ? 'new row' : 'row');
    return isCreate
      ? `Confirm create · ${slug} · ${label}`
      : `Confirm field updates · ${slug} · ${label}`;
  }, [waitRequest]);

  useEffect(() => {
    if (!open) {
      setBusy(false);
      setError('');
      setFieldChanges([]);
      return;
    }
    setError('');
    const changes = Array.isArray(waitRequest?.field_changes)
      ? waitRequest.field_changes.map((c) => ({
        ...c,
        included: c.included !== false,
      }))
      : [];
    setFieldChanges(changes);
  }, [open, waitRequest]);

  if (!open) return null;

  const toggleField = (key) => {
    setFieldChanges((prev) => prev.map((c) => (
      c.key === key ? { ...c, included: !c.included } : c
    )));
  };

  const handleCancel = async () => {
    setBusy(true);
    setError('');
    try {
      if (sessionId) {
        try { await clearEntryUpdateWaitRequest(sessionId); } catch (_) { /* ignore */ }
      }
      onClose?.();
    } finally {
      setBusy(false);
    }
  };

  const handleConfirm = async () => {
    if (!sessionId) return;
    setBusy(true);
    setError('');
    try {
      const data = await confirmEntryUpdateWait({
        sessionId,
        field_changes: fieldChanges,
      });
      onConfirmed?.(data);
      onClose?.();
    } catch (err) {
      setError(err.message || 'Failed to apply updates');
    } finally {
      setBusy(false);
    }
  };

  const before = waitRequest?.before_raw || {};
  const keys = [...new Set([
    ...Object.keys(before),
    ...fieldChanges.map((c) => c.key),
  ])].filter(Boolean).sort();

  return (
    <div className="conversation-ingest-overlay" role="dialog" aria-modal="true" aria-labelledby="entry-update-wait-title">
      <div className="conversation-ingest-modal" style={{ width: 'min(720px, 96vw)', minHeight: 'auto' }}>
        <div className="conversation-ingest-header">
          <h3 id="entry-update-wait-title">{title}</h3>
          <p className="conversation-ingest-hint" style={{ margin: '6px 0 0' }}>
            {(waitRequest?.create === true || waitRequest?.op === 'create')
              ? 'Review fields for the new row. Uncheck any field to skip it, then create.'
              : 'Review proposed field changes. Uncheck any field to skip it, then apply.'}
            {waitRequest?.reason ? (
              <>
                <br />
                <strong>Reason:</strong> {waitRequest.reason}
              </>
            ) : null}
          </p>
          <p className="conversation-ingest-hint">
            {waitRequest?.group_slug}
            {(waitRequest?.create === true || waitRequest?.op === 'create')
              ? ' · (new entry)'
              : `/${waitRequest?.entry_id}`}
          </p>
        </div>
        <div className="conversation-ingest-body">
          {error ? <p className="conversation-ingest-error" role="alert">{error}</p> : null}

          <div className="conversation-ingest-snapshot before">
            <div className="conversation-ingest-snapshot-label">Before</div>
            <div className="conversation-ingest-field-grid">
              {keys.map((key) => {
                const hasValue = Object.prototype.hasOwnProperty.call(before, key);
                return (
                  <div key={key} className="conversation-ingest-field-pair">
                    <span className="field-key">{key}:</span>
                    <span className="field-value">{hasValue ? formatFieldValue(before[key]) : '—'}</span>
                  </div>
                );
              })}
              {keys.length === 0 && (
                <span className="conversation-ingest-hint">(empty row)</span>
              )}
            </div>
          </div>

          <div className="conversation-ingest-snapshot after">
            <div className="conversation-ingest-snapshot-label">After (proposed)</div>
            <div className="conversation-ingest-field-grid">
              {fieldChanges.map((change) => {
                const kept = change.included !== false;
                return (
                  <label
                    key={change.key}
                    className={`conversation-ingest-field-pair has-toggle${kept ? ' changed' : ' rejected'}`}
                  >
                    <input
                      type="checkbox"
                      className="conversation-ingest-field-check"
                      checked={kept}
                      disabled={busy}
                      onChange={() => toggleField(change.key)}
                      aria-label={`Keep ${change.key}`}
                    />
                    <span className="field-key">{change.key}:</span>
                    <span className="field-value">{formatFieldValue(change.new_value)}</span>
                  </label>
                );
              })}
              {fieldChanges.length === 0 && (
                <span className="conversation-ingest-hint">(no proposed changes)</span>
              )}
            </div>
          </div>
        </div>
        <div className="conversation-ingest-footer" style={{ flexWrap: 'wrap', gap: 8 }}>
          <button type="button" className="btn ghost" onClick={handleCancel} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="btn"
            onClick={handleConfirm}
            disabled={busy || !fieldChanges.some((c) => c.included !== false)}
          >
            {busy
              ? 'Working…'
              : (waitRequest?.create === true || waitRequest?.op === 'create')
                ? 'Create entry'
                : 'Apply updates'}
          </button>
        </div>
      </div>
    </div>
  );
}
